import React, {useEffect, useState} from "react"
import SummaryListGroup from "./SummaryListGroup";
import SummaryQrCode from "../atoms/SummaryQrCode";
import DownloadPdfButton from "../atoms/DownloadPdfButton";
import {colorTargetReplacement} from "../../../../core/utils/replace";
import {fetchPartGroupItems} from "../../../../core/utils/partGroupUtils";
import {shallowEqual, useSelector} from "react-redux";
import callPeerRemoteModule from "../../../../core/utils/callPeerRemoteModule";
import {COLORWAY_DATA} from "../../../../core/constants/color";

const normalizeItem = (item, stepType, langData) => {
  if (Array.isArray(item)) {
    if (stepType === "COLOR") {
      return item.map(colorItem => ({
        name: colorItem.options?.CUSTOM
          ? "Custom Color"
          : colorItem.options?.[langData.langName] || "",
        code: colorItem.options?.CUSTOM
          ? `${colorItem.options.CUSTOM.DIFFUSE.r}, ${colorItem.options.CUSTOM.DIFFUSE.g}, ${colorItem.options.CUSTOM.DIFFUSE.b}`
          : colorItem.options?.code || "",
        title: colorItem.title,
        isCustom: !!colorItem.options?.CUSTOM,
        originalData: colorItem
      }));
    } else if (stepType === "PART_2D") {
      return item.map(partItem => ({
        name: langData.lang === "kr" ? partItem.nameKr : partItem.nameEn,
        code: partItem.partPersonalization?.code || "",
        title: partItem.partPersonalization?.[langData.langName] || "",
        originalData: partItem
      }));
    }
  }


  if (item.partGroupId) {
    const switchKey = item.assetType === "MATERIAL" ? "materialSwitch" : "partSwitch";
    item[switchKey] = {
      code: item.code,
      nameEn: item.nameEn,
      nameKr: item.nameKr,
    }
  }

  return {
    name: item[langData.langName] || "",
    code: item.customCode ||
      item.partSwitch?.code ||
      item.materialSwitch?.code ||
      item.code || "",
    color: item.color || null,
    colorName: item.colorName || null,
    originalData: item
  };
};

const SummaryBody = () => {

  const configData = useSelector(state => state.optionWindow.CONFIG_DATA);
  const navData = useSelector(state => state.menuOpen.navData);
  const langData = useSelector(state => state.theme.langData);
  const configId = useSelector(state => state.qrcode.configId);
  const connectMode = useSelector(state => state.theme.connectMode);
  const currentHeader = useSelector(state => state.optionWindow.currentHeader);
  const overlayPanelItem = useSelector(state => state.overlayPanel.overlayPanelItem);
  const currentVehicle = useSelector(state => state.optionWindow.currentVehicle);

  const navigationList = navData?.processedNavData || [];

  const [part, setPart] = useState({data: {}});

  useEffect(() => {
    if (overlayPanelItem !== "SUMMARY") {
      return;
    }

    const processSummary = async () => {
      const temp = {data: {}};
      if (!temp.data["VEHICLE MODEL"]) {
        temp.data["VEHICLE MODEL"] = {
          "PRODUCT": {
            "MODEL": {
              data: {
                name: `${currentVehicle.displayTitle} ${currentVehicle.displaySubtitle}`,
                code: '-',
                originalData: {
                  id: currentVehicle.id,
                  nameKr: `${currentVehicle.displayTitle} ${currentVehicle.displaySubtitle}`,
                  nameEn: `${currentVehicle.displayTitle} ${currentVehicle.displaySubtitle}`,
                  partSwitch: {
                    code: " - ",
                    nameKr: `${currentVehicle.displayTitle} ${currentVehicle.displaySubtitle}`,
                    nameEn: `${currentVehicle.displayTitle} ${currentVehicle.displaySubtitle}`,
                  }
                }
              },
              type: "PART_3D"
            }
          }
        };
      }

      // 1단계: 그룹 ID 수집
      const groupInfoMap = new Map();

      navigationList.forEach((nav) => {
        if (nav.isGnb || !nav.navigationSteps) return;

        nav.navigationSteps.forEach(stepObject => {
          const stepType = stepObject?.stepType;

          if (stepType === "PART_3D" || stepType === "MATERIAL") {
            stepObject.stepItems?.forEach(stepItem => {
              if (stepItem?.isSingle === false && stepItem.partGroupId) {
                groupInfoMap.set(stepItem.partGroupId, {
                  groupId: stepItem.partGroupId,
                  stepType: stepType
                });
              }
            });
          }
        });
      });

      // 2단계: 그룹 데이터 가져오기
      let fetchedGroupData = {};
      if (groupInfoMap.size > 0) {
        fetchedGroupData = await fetchPartGroupItems(
          currentVehicle.id,
          Array.from(groupInfoMap.values())
        );
      }



      // 3단계: 데이터 처리
      navigationList.forEach((nav) => {
        if (nav.isGnb) return;
        if (!nav.navigationSteps || nav.navigationSteps.length === 0) return;

        for (let stepObject of nav.navigationSteps) {
          const stepType = stepObject?.stepType;
          let itemsForColor = [];
          let itemsForPart2D = [];
          let singleItem = null;
          let key = "";

          for (let stepItem of stepObject.stepItems) {

            if (stepType === "PART_2D") {
              key = (stepObject.nameEn?.toUpperCase().replace(' ', '_')) + '_' + stepItem.partPersonalization?.code;
              if (configData[key]?.CODE === stepItem.partPersonalization?.code) {
                itemsForPart2D.push({...stepItem});
              }

            } else if (stepType === "COLOR") {
              key = colorTargetReplacement(stepItem.colorTarget);

              if (key === "INT_KEY") {
                const title = "COLORWAY";
                let colorwayData = COLORWAY_DATA[configData.COLORWAY];
                let selectedItem = {
                  type: "INT",
                  nameKr: colorwayData?.title,
                  nameEn: colorwayData?.title
                }

                itemsForColor.push({title: title, options: selectedItem});
              }


              if (configData[key]) {
                const title = stepItem[langData.langName];

                let selectedItem;
                if (configData[key].CUSTOM) {
                  selectedItem = configData[key];
                } else {
                  const findSelected = stepItem.itemOptions.find((color) => color.color?.code === configData[key]?.COLOR);
                  selectedItem = findSelected?.color;
                }
                itemsForColor.push({title: title, options: selectedItem});
              }


            } else if (stepType === "MATERIAL") {
              key = stepItem.materialSwitch?.type;

              // 단일 아이템
              if (stepItem.isSingle || stepItem.isSingle === undefined) {
                if (configData[key]?.CODE === stepItem.materialSwitch?.code) {
                  singleItem = {...stepItem};
                  if (configData[key]?.COLOR) {
                    singleItem.color = configData[key].COLOR;
                  }
                }
              }
              // 그룹 아이템
              else {
                const groupType = stepItem.groupType;
                if (groupType && configData[groupType]) {
                  const groupItems = fetchedGroupData[stepItem.partGroupId];
                  if (groupItems) {
                    const found = groupItems.find(item =>
                      item.materialSwitch?.code === configData[groupType].CODE ||
                      item.code === configData[groupType].CODE
                    );
                    if (found) {
                      singleItem = {...found};
                      if (configData[groupType]?.COLOR) {
                        singleItem.color = configData[groupType].COLOR;
                      }
                    }
                  }
                }
              }

            } else if (stepType === "PART_3D") {
              // 단일 아이템
              if (stepItem.isSingle || stepItem.isSingle === undefined) {
                key = stepItem.partSwitch?.partCategory;
                if (key && configData[key]?.CODE === stepItem.partSwitch?.code) {
                  singleItem = {...stepItem};
                  if (configData[key]?.COLOR) {
                    singleItem.color = configData[key].COLOR;
                    singleItem.colorName = configData[key].COLORNAME
                  }
                }
              }
              // 그룹 아이템
              else {
                const groupType = stepItem.groupType;
                if (groupType && configData[groupType]) {
                  const groupItems = fetchedGroupData[stepItem.partGroupId];
                  if (groupItems) {
                    const found = groupItems.find(item =>
                      item.code === configData[groupType].CODE
                    );
                    if (found) {
                      singleItem = {...found};
                      if (configData[groupType]?.COLOR) {
                        singleItem.color = configData[groupType].COLOR;
                      }
                    }
                  }
                }
              }
            }
          }

          // 결과 저장
          let finalItem = null;
          if (stepType === 'COLOR' && itemsForColor.length > 0) {
            finalItem = itemsForColor;
          } else if (stepType === 'PART_2D' && itemsForPart2D.length > 0) {
            finalItem = itemsForPart2D;
          } else if (singleItem !== null) {
            finalItem = singleItem;
          }

          if (finalItem !== null) {
            const stepTitle = stepObject[langData.langName];
            const cameraType = nav.cameraType;
            const navTitle = nav[langData.langName];

            const normalizedItem = normalizeItem(finalItem, stepType, langData);

            if (!temp.data[cameraType]) {
              temp.data[cameraType] = {};
            }

            if (!temp.data[cameraType][navTitle]) {
              temp.data[cameraType][navTitle] = {};
            }

            temp.data[cameraType][navTitle][stepTitle] = {
              type: stepType,
              data: normalizedItem
            };
          }
        }
      });
      

      setPart(temp);

      (async () => {
        const peer = await callPeerRemoteModule(connectMode);
        peer?.conn.sendData("summaryData", {data: temp, configId: configId, configData: configData});
      })();
    };

    processSummary();

  }, [currentHeader, overlayPanelItem, currentVehicle.id]);

  return (
    <div className={"summary-body"}>
      <div className={"summary-spec"}>
        {/*<div className={"spec-group"}>*/}
        {/*  <h2 style={{marginBottom: 0}}>Vehicle Model</h2>*/}
        {/*  <ul>*/}
        {/*    <li>*/}
        {/*      <div className={"spec-sub"}>*/}
        {/*        <div className={"spec-name"}>Model</div>*/}
        {/*        <div className={"spec-value"}>{`${currentVehicle.displayTitle} ${currentVehicle.displaySubtitle}`}</div>*/}
        {/*        <div className={"spec-code"}> - </div>*/}
        {/*      </div>*/}
        {/*    </li>*/}
        {/*  </ul>*/}
        {/*</div>*/}

        <SummaryListGroup items={part.data}/>
      </div>
      <div className={"summary-action"}>
        <SummaryQrCode/>
        <DownloadPdfButton summaryConfigData={part.data}/>
      </div>
    </div>
  )
}

export default SummaryBody;
